import { create } from "zustand";
import type {
  ItemEditorViewContext,
  UserViewContext,
} from "@forgepoint/shared";

/** One open screen's report, keyed by the component instance that made it. */
interface ReportedView {
  key: string;
  view: ItemEditorViewContext;
}

/** State and actions for the shared what-is-on-screen snapshot. */
interface ViewContextState {
  /** Reports from every open screen, outermost first; the last one is on top. */
  reported: ReportedView[];
  /** What the user has on top of the screen, null when no reporting screen is open. */
  view: UserViewContext | null;
  /**
   * Reports one editor instance's own fields under its key; merges over that
   * instance's report for the same item so tab-owned fields survive, replaces
   * it otherwise. A first report goes on top.
   */
  upsertView: (key: string, view: ItemEditorViewContext) => void;
  /** Merges tab-owned fields into the top report. No-op when nothing is reported. */
  mergeView: (partial: Partial<Omit<ItemEditorViewContext, "screen">>) => void;
  /** Drops one instance's report when its screen closes or unmounts. */
  clearView: (key: string) => void;
}

const withReported = (reported: ReportedView[]) => ({
  reported,
  view: reported.at(-1)?.view ?? null,
});

/**
 * Store holding a live snapshot of what the user is viewing, reported by each
 * open screen (today: Item Editor V2 and its tabs) and read by the chat
 * composer at send time so the agent gets the view context stamped onto the
 * message. Editors nest — a BOM row opens its component in another editor on
 * top — so each open instance keeps its own report, the newest one is what the
 * user sees, and closing it hands the view back to the editor underneath.
 */
export const useViewContextStore = create<ViewContextState>((set) => ({
  reported: [],
  view: null,
  upsertView: (key, view) =>
    set((state) => {
      const current = state.reported.find((entry) => entry.key === key);
      const next: ReportedView = {
        key,
        view:
          current?.view.itemId === view.itemId
            ? { ...current.view, ...view }
            : view,
      };
      return withReported(
        current
          ? state.reported.map((entry) => (entry.key === key ? next : entry))
          : [...state.reported, next],
      );
    }),
  mergeView: (partial) =>
    set((state) => {
      const top = state.reported.at(-1);
      if (!top) {
        return state;
      }
      return withReported([
        ...state.reported.slice(0, -1),
        { key: top.key, view: { ...top.view, ...partial } },
      ]);
    }),
  clearView: (key) =>
    set((state) =>
      state.reported.some((entry) => entry.key === key)
        ? withReported(state.reported.filter((entry) => entry.key !== key))
        : state,
    ),
}));
